export const getFromParam = (url: string): string | null => {
    try {
        return new URL(url).searchParams.get('from');
    } catch {
        return null;
    }
};

/**
 * If the URL is a redirect URL (contains `/redirect` with a `dest` query param),
 * extracts and returns the decoded destination path. Otherwise returns null.
 */
export const extractDestFromRedirectUrl = (url: string): string | null => {
    try {
        const parsed = new URL(url, 'https://placeholder.com');
        if (parsed.pathname.includes('/redirect') && parsed.searchParams.has('dest')) {
            return decodeURIComponent(parsed.searchParams.get('dest')!);
        }
        return null;
    } catch {
        return null;
    }
};

export const prependBaseUrl = (baseUrl: string, url: string | undefined | null): string => {
    return new URL(url ?? '', baseUrl).toString();
};

/** Expects absolute jobClickUrl (e.g. from prependBaseUrl). */
const getJobClickParams = (
    jobClickUrl: string | undefined | null,
): { trackingKey: string; adBlob: string; xkcb: string; from: string } => {
    if (!jobClickUrl) {
        console.error('[urlUtils] getJobClickParams: jobClickUrl is missing or falsy');
        return { trackingKey: '', adBlob: '', xkcb: '', from: '' };
    }

    try {
        const urlObject = new URL(jobClickUrl);
        const trackingKey = urlObject.searchParams.get('tk') ?? '';
        const adBlob = urlObject.searchParams.get('ad') ?? '';
        const xkcb = urlObject.searchParams.get('xkcb') ?? '';
        const from = urlObject.searchParams.get('from') ?? '';

        return { trackingKey, adBlob, xkcb, from };
    } catch (e) {
        console.error('[urlUtils] getJobClickParams: failed to parse jobClickUrl', jobClickUrl, e);
        return { trackingKey: '', adBlob: '', xkcb: '', from: '' };
    }
};

export const composeViewJobLink = (
    baseUrl: string,
    jobKey: string,
    jobClickUrl: string | undefined | null,
): string => {
    try {
        const viewJobUrl = new URL('/m/viewjob', baseUrl);
        viewJobUrl.searchParams.set('jk', jobKey);

        const { trackingKey, adBlob, xkcb, from } = getJobClickParams(jobClickUrl);
        if (trackingKey) {
            viewJobUrl.searchParams.set('tk', trackingKey);
        }
        if (adBlob) {
            viewJobUrl.searchParams.set('ad', adBlob);
        }
        if (xkcb) {
            viewJobUrl.searchParams.set('xkcb', xkcb);
        }

        viewJobUrl.searchParams.set('from', from || 'hp');

        return viewJobUrl.toString();
    } catch (e) {
        console.error('[urlUtils] composeViewJobLink: failed to compose ViewJobLink', e);
        throw new Error('[urlUtils] Compose ViewJobLink: failed to compose ViewJobLink');
    }
};

export const withFromParam = (url: string, from: string | null | undefined): string => {
    try {
        const parsed = new URL(url);
        parsed.searchParams.set('from', from ?? 'hp');
        return parsed.toString();
    } catch {
        return url;
    }
};

const SPONSORED_JOB_URL_PATH = '/pagead/clk';

export function isJobSponsored(jobClickUrl: string | undefined | null): boolean {
    return jobClickUrl?.includes(SPONSORED_JOB_URL_PATH) ?? false;
}

export const prepareJobClickUrl = (url: string | undefined | null): string | undefined => {
    if (!url) {
        return undefined;
    }

    const secured = url.replace(/^http:\/\//, 'https://');
    try {
        const parsed = new URL(secured);
        if (!parsed.searchParams.has('vjs')) {
            parsed.searchParams.set('vjs', '3');
        }
        return parsed.toString();
    } catch {
        return secured;
    }
};
